import { useEffect, useMemo, useRef, useState } from "react";
import { useHotkeys } from "react-hotkeys-hook";
import Fuse from "fuse.js";
import useBooleanState from "src/hooks/useBooleanState";
import { useSearchEntities } from "src/queries/search/search.query";
import type { EntityType } from "src/queries/search/search.types";
import { useGlobalLocalContext } from "src/storage/localStorage.ts";
import { ENTITY_TYPES, SEARCH_MODAL } from "./SearchModal.constants";

export function useSearchModal() {
  const [isSearchOpen, setIsSearchOpen, setIsSearchClosed] = useBooleanState(false);

  // https://react-hotkeys-hook.vercel.app/docs/documentation/useHotkeys/basic-usage#modifiers--special-keys
  useHotkeys("meta+k", (e) => {
    e.preventDefault();
    setIsSearchOpen();
  });

  return {
    isSearchOpen,
    openSearch: setIsSearchOpen,
    closeSearch: setIsSearchClosed,
  };
}

export function useSearchModalLogic(open: boolean) {
  const inputRef = useRef<HTMLInputElement>(null);

  const { data: fuse, isLoading } = useSearchEntities({ enabled: open });

  const [searchQuery, setSearchQuery] = useState("");
  const [selectedEntityType, setSelectedEntityType] = useState<EntityType | null>(
    null
  );

  // Create Fuse instance for entity types
  const entityTypeFuse = useMemo(
    () =>
      new Fuse(ENTITY_TYPES, {
        keys: ["label", "type"],
        threshold: 0.3,
        includeScore: true,
        includeMatches: true,
        minMatchCharLength: 3,
      }),
    []
  );

  // Focus input when modal opens
  useEffect(() => {
    if (open) {
      const timeoutId = setTimeout(() => {
        inputRef.current?.focus();
      }, SEARCH_MODAL.INPUT_FOCUS_DELAY);

      return () => clearTimeout(timeoutId);
    }
  }, [open]);

  // Reset search query and selected type when modal closes
  useEffect(() => {
    if (!open) {
      setSearchQuery("");
      setSelectedEntityType(null);
    }
  }, [open]);

  // Search for entity types
  const entityTypeResults = useMemo(() => {
    if (selectedEntityType || !searchQuery.trim()) {
      return [];
    }

    return entityTypeFuse.search(searchQuery);
  }, [entityTypeFuse, searchQuery, selectedEntityType]);

  // Perform fuzzy search on entities
  const entityResults = useMemo(() => {
    if (!fuse || !searchQuery.trim()) {
      return [];
    }

    let results = fuse.search(searchQuery);

    // Filter by selected entity type if one is selected
    if (selectedEntityType) {
      results = results.filter((result) => result.item.type === selectedEntityType);
    }

    return results.slice(0, SEARCH_MODAL.MAX_RESULTS);
  }, [fuse, searchQuery, selectedEntityType]);

  // Get recent objects from localStorage
  const recentObjects = useGlobalLocalContext((s) => s.recentObjects);

  // Show recent objects when search query is empty
  const showRecentObjects = !searchQuery.trim() && !selectedEntityType;

  return {
    inputRef,
    searchQuery,
    setSearchQuery,
    selectedEntityType,
    setSelectedEntityType,
    entityTypeResults,
    entityResults,
    isLoading,
    recentObjects,
    showRecentObjects,
  };
}
